import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import TagManager from 'react-gtm-module'

import ErrorPage from './pages/Error';
import Layout from './layout/Layout';
import DashboardPage from './pages/Dashboard';
import Authpage from './pages/AuthPage';
import PatientDetails from './pages/PatientDetails';
import CasesPage from './pages/Cases';
import CalendarPage from './pages/Calendar';
import TasksPage from './pages/Tasks';
import DocumentsPage from './pages/Documents';
import ReportsPage from './pages/Reports';
import SystemPage from './pages/System';
import RootLayout from './layout/RootLayout';
import { IsUserAuth } from './lib/loaders';
import WorkQueueLayout from './layout/WorkQueueLayout';
import GeocodeTest from './pages/GeocodeTest';
import WorkQ from './pages/WorkQ';
import ReferralsPage from './pages/ReferralsPage';
import PDFPrintRedirect from './pages/PDFPrintRedirect';
import PrintPageServerDetails from './pages/PrintPageServerDetails';
import { printLoaderRedirect } from './lib/printLoader';
import HistoryPage from './pages/HistoryPage';
import ExportsPage from './pages/ExportsPage';
import ReleaseNotesPage from './pages/ReleaseNotesPage';
import TransfersPage from './pages/TransfersPage';
import TokenLoginPage from './pages/TokenLoginPage';
import { runOneTimeWorkQFilterReset } from './lib/filters';
import * as Sentry from '@sentry/react';

const tagManagerArgs = {
    gtmId: process.env.REACT_APP_GTM
}

function Index() {

    const environment = process.env.REACT_APP_ENVIRONMENT_SENTRY

    if (environment !== 'development') {
        Sentry.init({
            dsn: "https://fa38818b5cb672baa6c95ea2aad3a122@o4511614529372160.ingest.us.sentry.io/4511614541037568",
            dataCollection: {
              // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
              // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
              // userInfo: false,
              // httpBodies: []
            },
            integrations: [
              Sentry.browserTracingIntegration(),
              Sentry.replayIntegration()
            ],
            // Tracing
            tracesSampleRate: 1.0, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: ["localhost", /^https:\/\/ecm-admin\.proco-llc\.com\/api/],
       
            // Session Replay
            replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
            // Enable logs to be sent to Sentry
            enableLogs: true,
          });
    }
    

    useEffect(() => {
        runOneTimeWorkQFilterReset()

        if (navigator) {
            let userLang = navigator.language || navigator.userLanguage;
            
            if (userLang === 'en' || userLang === 'en-US') {
                try {
                    TagManager.initialize(tagManagerArgs);
                } catch (error) {
                    console.error('Failed to initialize GTM:', error);
                }
            }
        }
        
    }, [])

    const router = createBrowserRouter([
        {
            path: '/',   
            errorElement: <ErrorPage />,
            element: <RootLayout />,            
            children: [
                {index: true, element: <Authpage />},
                {path: '/frontend-login', element: <TokenLoginPage />},
                {
                    element: <Layout />,                     
                    children: [
                        {path: '/dashboard', element: <DashboardPage />, loader: IsUserAuth},  
                        {path: '/work-queues', 
                            element: <WorkQueueLayout />, 
                            loader: IsUserAuth, 
                            children: [
                                {index: true, element: <CasesPage />, loader: IsUserAuth},
                                {path: 'police-report', children: [
                                    {index: true, element: <WorkQ />, loader: IsUserAuth},
                                    {path: ':patientId', element: <WorkQ />, loader: IsUserAuth},
                                ]},
                                {path: 'drivers-license-mismatch', children: [
                                    {index: true, element: <WorkQ />, loader: IsUserAuth},
                                    {path: ':patientId', element: <WorkQ />, loader: IsUserAuth},
                                ]},
                                {path: 'invalid-fax-q', children: [
                                    {index: true, element: <WorkQ />, loader: IsUserAuth},
                                    {path: ':patientId', element: <WorkQ />, loader: IsUserAuth},
                                ]},
                                { path: ':workqSlug', children: [
                                    {index: true, element: <WorkQ />, loader: IsUserAuth},
                                    {path: ':patientId', element: <PatientDetails />, loader: IsUserAuth},
                                ]}
                            ]
                        },   
                        {path: '/cases', element: <WorkQueueLayout noWorkQ={true} />, children: [
                            { index: true, path: ':patientId', element: <PatientDetails noWorkQ={true} />, loader: IsUserAuth},
                        ]},   
                        // {path: '/calendar', element: <CalendarPage />, loader: IsUserAuth},
                        {path: '/tasks', element: <TasksPage />, loader: IsUserAuth},
                        // {path: '/documents', element: <DocumentsPage />, loader: IsUserAuth},
                        // {path: '/referrals', element: <ReferralsPage />, loader: IsUserAuth},
                        {path: '/transfers', element: <TransfersPage />, loader: IsUserAuth},
                        {path: '/history', children: [
                            { index: true, element: <HistoryPage />, loader: IsUserAuth},
                            { path: ':caseId', element: <HistoryPage />, loader: IsUserAuth},
                        ]}, 
                        // {path: '/reports', element: <ReportsPage />, loader: IsUserAuth},
                        {path: '/exports', element: <ExportsPage />, loader: IsUserAuth},
                        {path: '/release-notes', element: <ReleaseNotesPage />, loader: IsUserAuth},
                        // {path: '/system', element: <SystemPage />, loader: IsUserAuth},                       
                        
                    ]
                },          

                {path: '/pdf-print', children: [
                    {index: true, element: <PDFPrintRedirect />, loader: printLoaderRedirect},
                    { path: ':varSlug', children: [
                        {index: true, element: <PDFPrintRedirect />, loader: printLoaderRedirect},
                        { path: ':caseId', element: <PrintPageServerDetails /> },
                    ]},
                ]},

                { path: '/test/geocode', element: <GeocodeTest /> },
              
                
            ]
        }
    ])

    return <RouterProvider router={router} />;
}

function App() {
    return (
        <div className="App">
            <Index />
        </div>
    );
}

export default App;
